import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, LogOut, Camera, Save, X, ArrowLeft, ChevronRight, Bell, Smartphone, Info } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import { NotificationDiagnostics } from "@/components/NotificationDiagnostics";
import { PWAInstallGuide } from "@/components/PWAInstallPrompt";
import { AppVersionCard } from "@/components/AppVersionCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const UAE_EMIRATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

export default function ProfilePage() {
  const { user, profile, updateProfile, signOut, loading: authLoading, isAgent } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "+971",
    address_line1: "",
    address_line2: "",
    city: "Dubai",
    emirate: "Dubai",
    postal_code: "",
    marketing_opt_in: false,
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        phone: profile.phone || "+971",
        address_line1: profile.address_line1 || "",
        address_line2: profile.address_line2 || "",
        city: profile.city || "Dubai",
        emirate: profile.emirate || "Dubai",
        postal_code: profile.postal_code || "",
        marketing_opt_in: profile.marketing_opt_in || false,
      });
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmirateChange = (value: string) => {
    setFormData((prev) => ({ ...prev, emirate: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    const { error } = await updateProfile(formData);
    if (error) {
      toast.error("Échec de la mise à jour du profil");
    } else {
      toast.success("Profil mis à jour !");
      setIsEditing(false);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        phone: profile.phone || "+971",
        address_line1: profile.address_line1 || "",
        address_line2: profile.address_line2 || "",
        city: profile.city || "Dubai",
        emirate: profile.emirate || "Dubai",
        postal_code: profile.postal_code || "",
        marketing_opt_in: profile.marketing_opt_in || false,
      });
    }
    setIsEditing(false);
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success("Déconnexion réussie");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const initials = `${formData.first_name?.charAt(0) || ""}${formData.last_name?.charAt(0) || ""}`.toUpperCase();

  return (
    <div className="min-h-screen flex flex-col pb-24 bg-background">
      {/* Hero Header with gradient */}
      <header 
        className="relative overflow-hidden px-4 sm:px-6 pt-6 sm:pt-8 pb-6 sm:pb-8"
        style={{ paddingTop: 'max(1.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="relative max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            {isAgent && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/agent")}
                className="shrink-0 h-9 w-9"
              >
                <ArrowLeft size={18} />
              </Button>
            )}
            <h1 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
              Mon Profil
            </h1>
          </div>

          {/* Avatar Card */}
          <div className="bg-card rounded-2xl border border-border p-5 sm:p-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border-2 border-primary/30">
                  <span className="text-primary font-serif text-xl sm:text-2xl font-semibold">
                    {initials || "?"}
                  </span>
                </div>
                <button className="absolute -bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center shadow-gold border-2 border-background">
                  <Camera size={12} className="text-primary-foreground" />
                </button>
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-lg sm:text-xl font-medium text-foreground">
                  {formData.first_name} {formData.last_name}
                </h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 space-y-4 sm:space-y-5 overflow-auto">
        <div className="max-w-2xl mx-auto space-y-4 sm:space-y-5">
          {/* Edit Mode Actions */}
          {isEditing && (
            <div className="flex gap-2 animate-fade-in">
              <Button variant="outline" className="flex-1" onClick={handleCancel} disabled={loading}>
                <X size={16} className="mr-2" />
                Annuler
              </Button>
              <Button variant="gold" className="flex-1" onClick={handleSave} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                Enregistrer
              </Button>
            </div>
          )}

          {/* Profile Form */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-base sm:text-lg font-medium text-foreground">
                Informations personnelles
              </h3>
              {!isEditing && (
                <Button variant="ghost" size="sm" className="text-xs sm:text-sm text-primary" onClick={() => setIsEditing(true)}>
                  Modifier
                </Button>
              )}
            </div>

            <div className="bg-card rounded-xl border border-border p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-xs">Prénom</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted/50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-xs">Nom</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted/50" : ""}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-xs">Téléphone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted/50" : ""}
                />
              </div>
            </div>
          </section>

          {/* Address Section */}
          <section className="space-y-3">
            <h3 className="font-serif text-base sm:text-lg font-medium text-foreground">Adresse</h3>

            <div className="bg-card rounded-xl border border-border p-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address_line1" className="text-xs">Adresse</Label>
                <Input
                  id="address_line1"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted/50" : ""}
                  placeholder="Numéro et nom de rue"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_line2" className="text-xs">Complément</Label>
                <Input
                  id="address_line2"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted/50" : ""}
                  placeholder="Appartement, bâtiment, etc."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-xs">Ville</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted/50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emirate" className="text-xs">Émirat</Label>
                  <Select value={formData.emirate} onValueChange={handleEmirateChange} disabled={!isEditing}>
                    <SelectTrigger className={!isEditing ? "bg-muted/50" : ""}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UAE_EMIRATES.map((emirate) => (
                        <SelectItem key={emirate} value={emirate}>{emirate}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="postal_code" className="text-xs">Code postal</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted/50" : ""}
                />
              </div>
            </div>
          </section>

          {/* Settings Cards */}
          <section className="space-y-3">
            <h3 className="font-serif text-base sm:text-lg font-medium text-foreground">Paramètres</h3>
            
            <div className="space-y-2">
              {/* Notifications Card */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Bell size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm">Notifications</h4>
                    <p className="text-xs text-muted-foreground">Gérer les alertes push</p>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground shrink-0" />
                </div>
                <div className="px-4 pb-4 pt-0">
                  <NotificationDiagnostics />
                </div>
              </div>

              {/* Application Card */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Smartphone size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm">Application</h4>
                    <p className="text-xs text-muted-foreground">Installation et mises à jour</p>
                  </div>
                </div>
                <div className="px-4 pb-4 pt-0 space-y-3">
                  <PWAInstallGuide />
                  <AppVersionCard />
                </div>
              </div>
            </div>
          </section>

          {/* Sign Out Section - with proper spacing from bottom nav */}
          <section className="pt-2 pb-4">
            <Button
              variant="outline"
              className="w-full border-destructive/30 text-destructive hover:bg-destructive/10 hover:border-destructive/50"
              onClick={handleSignOut}
            >
              <LogOut size={16} className="mr-2" />
              Se déconnecter
            </Button>
          </section>
        </div>
      </main>

      {!isAgent && <BottomNav />}
    </div>
  );
}